import { useEffect, useState } from "react";
import { $getRoot } from "lexical";
import { $generateHtmlFromNodes } from "@lexical/html";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { ClearEditorPlugin } from "@lexical/react/LexicalClearEditorPlugin";
import { TablePlugin } from "@lexical/react/LexicalTablePlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { CheckListPlugin } from "@lexical/react/LexicalCheckListPlugin";
import { HorizontalRulePlugin } from "@lexical/react/LexicalHorizontalRulePlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { TabIndentationPlugin } from "@lexical/react/LexicalTabIndentationPlugin";
import clsx from "clsx";

import { LinkPlugin } from "../plugins/LinkPlugin";
import { ComponentPickerMenuPlugin } from "../plugins/ComponentPickerPlugin";
import { FloatingLinkEditorPlugin } from "../plugins/FloatingLinkEditorPlugin";
import { AutoLinkPlugin } from "../plugins/AutoLinkPlugin";
import { MarkdownPastePlugin } from "../plugins/MarkdownPastePlugin";
import { TableCellActionMenuPlugin } from "../plugins/TableActionMenuPlugin";
import { CheckListKeyboardPlugin } from "../plugins/CheckListKeyboardPlugin";
import { EquationsPlugin } from "../plugins/EquationsPlugin";
import { MathMarkdownPlugin } from "../plugins/MathMarkdownPlugin";
import CodeHighlightPrismPlugin from "../plugins/CodeHightlistPrism";

interface LexicalEditorProps {
  classNames?: {
    content?: string;
  };
  isEditable?: boolean;
  isLinkEditMode: boolean;
  onChange?: (text_content: string | null, html_content: string | null) => void;
  onSetIsLinkEditMode: (isLinkEditMode: boolean) => void;
}

export function LexicalEditor({
  classNames,
  isEditable = true,
  isLinkEditMode,
  onChange,
  onSetIsLinkEditMode,
}: LexicalEditorProps) {
  const [editor] = useLexicalComposerContext();
  const [floatingAnchorElem, setFloatingAnchorElem] = useState<HTMLDivElement>();

  const onRef = (_floatingAnchorElem: HTMLDivElement) => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem);
    }
  };

  useEffect(() => {
    editor.setEditable(isEditable);
  }, [editor, isEditable]);

  useEffect(() => {
    return editor.registerUpdateListener(() => {
      editor.getEditorState().read(() => {
        const root = $getRoot();
        const textContent = root.getTextContent();

        onChange?.(textContent ?? null, textContent ? $generateHtmlFromNodes(editor) : null);
      });
    });
  }, [editor, onChange]);

  return (
    <div className="relative h-full">
      <RichTextPlugin
        contentEditable={
          <div ref={onRef}>
            <ContentEditable
              className={clsx(
                "relative h-full overflow-auto focus:outline-none [&>*:first-child]:mt-0!",
                classNames?.content
              )}
            />
          </div>
        }
        placeholder={
          isEditable ? (
            <span className="text-default-500 pointer-events-none absolute top-1 z-[-1]">Take a note...</span>
          ) : undefined
        }
        ErrorBoundary={LexicalErrorBoundary}
      />
      {/* Plugins */}
      <AutoLinkPlugin />
      <CheckListPlugin />
      <CheckListKeyboardPlugin />
      <ClearEditorPlugin />
      <ComponentPickerMenuPlugin />
      <EquationsPlugin />
      <MathMarkdownPlugin />
      <TabIndentationPlugin maxIndent={3} />
      {floatingAnchorElem && (
        <>
          <FloatingLinkEditorPlugin
            anchorElem={floatingAnchorElem}
            isLinkEditMode={isLinkEditMode}
            setIsLinkEditMode={onSetIsLinkEditMode}
          />
          <TableCellActionMenuPlugin anchorElem={floatingAnchorElem} cellMerge={true} />
        </>
      )}
      <HistoryPlugin />
      <HorizontalRulePlugin />
      <CodeHighlightPrismPlugin />
      <LinkPlugin />
      <ListPlugin />
      <TablePlugin hasCellBackgroundColor={false} hasCellMerge={false} hasHorizontalScroll />
      <MarkdownShortcutPlugin />
      <MarkdownPastePlugin />
    </div>
  );
}
